import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthFlowFrame from "../../components/AuthFlowFrame";
import { AuthFlowButton, FacebookPage } from "../../types";
import { showError } from "../../helpers";
import { getFacebookPages, saveFacebookPage } from "../../services";
import { Content } from "@jobber/components/Content";
import { RadioGroup, RadioOption } from "@jobber/components/RadioGroup";
import { Spinner } from "@jobber/components/Spinner";
import { Text } from "@jobber/components/Text";
import styles from "./JobberFacebookStep2.module.scss";

function Step2() {
    const [pages, setPages] = useState<FacebookPage[] | null>(null);
    const [selectedPageId, setSelectedPageId] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const state = sessionStorage.getItem("state");
                if (!state) {
                    throw new Error("Missing session state — please restart the setup flow.");
                }
                setPages(await getFacebookPages(state));
            } catch (e: any) {
                showError("Error loading Facebook Pages: " + e.message);
            }
        })();
    }, []);

    function handleContinue(e: React.MouseEvent) {
        e.preventDefault();
        const page = pages?.find(p => p.id === selectedPageId);
        if (!page) return;

        setIsSubmitting(true);
        (async () => {
            try {
                const state = sessionStorage.getItem("state");
                if (!state) throw new Error("Missing session state");
                await saveFacebookPage(state, page);
                sessionStorage.setItem("selected_page_name", page.name);
                navigate("/jobber-facebook-step3", { replace: true });
            } catch (e: any) {
                showError("Error saving page selection: " + e.message);
                setIsSubmitting(false);
            }
        })();
    }

    const button: AuthFlowButton = {
        link: "#",
        text: isSubmitting ? "Saving..." : "Continue",
        type: "primary",
        disabled: !selectedPageId,
        loading: isSubmitting,
        onClick: handleContinue,
    };

    return (
        <AuthFlowFrame
            pretitle={"Let's get you set up"}
            title={"Select your Facebook Page"}
            step={2}
            totalSteps={3}
            button={button}
        >
            <Content spacing={"base"}>
                {pages === null ? (
                    <Spinner />
                ) : (
                    <div className={styles.pageList}>
                        <RadioGroup
                            ariaLabel={"Facebook Pages"}
                            value={selectedPageId}
                            onChange={(v) => setSelectedPageId(String(v))}
                        >
                            {pages.map((page) => (
                                <RadioOption key={page.id} value={page.id}>
                                    <Text size={"large"}>{page.name}</Text>
                                </RadioOption>
                            ))}
                        </RadioGroup>
                    </div>
                )}
            </Content>
        </AuthFlowFrame>
    );
}

export default Step2;
